/**
 * price_at — the Preisspiegel price-resolution contract (#2388).
 *
 * v1 is a pure, client-side implementation over the mock series layer; a
 * server-side metric endpoint is a deferred follow-up. It answers "what was
 * the price of this item, on this side, for these axes, on this date?" with an
 * explicit provenance:
 *
 *   - date ON an observed point            → basis 'observed',     method 'exact'
 *   - date BETWEEN two observed points     → basis 'interpolated', method 'linear_interpolation'
 *                                            (linear, PER SERIES — never across counterparties)
 *   - date AFTER the last observed point    → basis 'forecast',    method 'last_known'
 *                                            (last-known persistence, flat forever)
 *   - date BEFORE the first observed point  → value null,          method 'out_of_range_before'
 *                                            (NO backward extrapolation)
 *   - no data at all                        → value null,          method 'no_data'
 *
 * The forecast in v1 is deliberately just last-known persistence; the pluggable
 * forecasting service is present as an UNWIRED stub (``forecasterStub``) that
 * the app never calls — contract-tested only.
 */
import type { PriceSide, Quality, Channel } from './catalog';
import type { ObservedPoint, PriceSeries } from './types';

export type PriceBasis = 'observed' | 'interpolated' | 'forecast';

export type PriceMethod =
  | 'exact'
  | 'linear_interpolation'
  | 'last_known'
  | 'out_of_range_before'
  | 'no_data';

/** The observed anchor(s) a result was derived from (its provenance). */
export interface PriceAnchor {
  date: string;
  value: number;
}

export interface PriceAtResult {
  /** Resolved value in the item's unit, or null when out of range / no data. */
  value: number | null;
  /** How the value was obtained; null when there is no value. */
  basis: PriceBasis | null;
  method: PriceMethod;
  /** The observed points the value was derived from. */
  provenance: PriceAnchor[];
}

/** ``YYYY-MM-DD`` → UTC epoch-day integer (whole days; DST-safe). */
export function toEpochDay(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return Math.floor(Date.UTC(y, m - 1, d) / 86_400_000);
}

/** Points sorted ascending by date (defensive copy — callers may pass unsorted). */
function sortedPoints(points: ObservedPoint[]): ObservedPoint[] {
  return [...points].sort((a, b) => toEpochDay(a.date) - toEpochDay(b.date));
}

/**
 * Resolve the price of ONE series at ``date``. This is the load-bearing core:
 * interpolation is strictly within a single series (never averaged across
 * counterparties). Mon 10 € / Fri 20 € ⇒ Wed ≈ 15 €, basis 'interpolated',
 * both anchors in provenance.
 */
export function priceAtSeries(points: ObservedPoint[], date: string): PriceAtResult {
  const pts = sortedPoints(points);
  if (pts.length === 0) {
    return { value: null, basis: null, method: 'no_data', provenance: [] };
  }

  const t = toEpochDay(date);
  const first = pts[0];
  const last = pts[pts.length - 1];

  // Before the first observation — no backward extrapolation.
  if (t < toEpochDay(first.date)) {
    return { value: null, basis: null, method: 'out_of_range_before', provenance: [{ ...first }] };
  }

  // After the last observation — last-known persistence (flat forecast).
  if (t > toEpochDay(last.date)) {
    return { value: last.value, basis: 'forecast', method: 'last_known', provenance: [{ ...last }] };
  }

  // Exactly on an observed point.
  const exact = pts.find((p) => toEpochDay(p.date) === t);
  if (exact) {
    return { value: exact.value, basis: 'observed', method: 'exact', provenance: [{ ...exact }] };
  }

  // Between two observed points — linear interpolation.
  let prev = first;
  let next = last;
  for (let i = 0; i < pts.length - 1; i++) {
    if (toEpochDay(pts[i].date) < t && t < toEpochDay(pts[i + 1].date)) {
      prev = pts[i];
      next = pts[i + 1];
      break;
    }
  }
  const t0 = toEpochDay(prev.date);
  const t1 = toEpochDay(next.date);
  const frac = (t - t0) / (t1 - t0);
  const value = prev.value + (next.value - prev.value) * frac;
  return {
    value,
    basis: 'interpolated',
    method: 'linear_interpolation',
    provenance: [{ ...prev }, { ...next }],
  };
}

/** Axis selection narrowing an item's series down to a single line. */
export interface AxisSelection {
  quality?: Quality;
  channel?: Channel;
  /** Pin a specific counterparty; required to disambiguate a multi-source item. */
  counterparty?: string;
}

/**
 * Resolve the series that ``price_at`` should read for (side, axes). Filters by
 * side, then by any provided axis. If several counterparties still match and
 * none was pinned, the series with the MOST observations wins (deterministic,
 * most-informative), tie-broken by ``seriesId`` — the result is still a single
 * series, so interpolation never crosses counterparties.
 *
 * **The tie-break is `seriesId`, not a locale-collated name** (HOF-280). Same
 * defect class as {@link tileModel.primarySeries} and fixed the same way, for two
 * reasons: it is the same surface, and `price_at` is the seam HOF-21 reads a
 * Leistung-Posten's price through — a second locale-dependent series selector left
 * standing here is how the defect recurs. Behaviourally inert today (`price_at` has
 * no live caller), which is precisely why it would have gone unnoticed.
 */
export function resolveSeries(
  series: PriceSeries[],
  side: PriceSide,
  axes: AxisSelection = {},
): PriceSeries | null {
  let candidates = series.filter((s) => s.side === side);
  if (axes.quality != null) candidates = candidates.filter((s) => s.quality === axes.quality);
  if (axes.channel != null) candidates = candidates.filter((s) => s.channel === axes.channel);
  if (axes.counterparty != null) candidates = candidates.filter((s) => s.counterparty === axes.counterparty);
  if (candidates.length === 0) return null;
  return [...candidates].sort(
    (a, b) => b.points.length - a.points.length || (a.seriesId < b.seriesId ? -1 : a.seriesId > b.seriesId ? 1 : 0),
  )[0];
}

/**
 * ``price_at(item, date, side, axes)`` — resolve a single item's price on a
 * given date/side/axes. ``item`` is passed as its resolved series set (the app
 * hydrates these from the mock layer). Delegates to {@link priceAtSeries} once
 * a single series is picked, so the basis/method/provenance contract holds.
 */
export function price_at(
  series: PriceSeries[],
  date: string,
  side: PriceSide,
  axes: AxisSelection = {},
): PriceAtResult {
  const resolved = resolveSeries(series, side, axes);
  if (!resolved) return { value: null, basis: null, method: 'no_data', provenance: [] };
  return priceAtSeries(resolved.points, date);
}

// ── Forecasting service — UNWIRED stub (deferred) ─────────────────────────────
// A placeholder for the real forecasting model (a §7 follow-up). The v1 chart's
// forward line is last-known persistence from ``price_at`` — this stub is NEVER
// called by the app. It exists so the seam is stable and contract-tested.

export interface ForecastRequest {
  /** Observed history to extrapolate from, ascending by date. */
  points: ObservedPoint[];
  /** How far past ``asOf`` to project, in days. */
  horizonDays: number;
  /** Anchor date ("Heute"), ISO ``YYYY-MM-DD``. */
  asOf: string;
}

export interface ForecastPoint {
  date: string;
  value: number;
}

export interface ForecastResult {
  points: ForecastPoint[];
  /** Model identifier — the stub reports ``unwired_stub``. */
  method: string;
  basis: 'forecast';
}

/** The pluggable forecasting seam. Real implementations arrive in a follow-up. */
export interface Forecaster {
  forecast(request: ForecastRequest): ForecastResult;
}

/**
 * The v1 stub. Intentionally UNWIRED — it returns no forecast points and is
 * never invoked by the page (which uses last-known persistence instead). Kept
 * to lock the ``Forecaster`` contract; see ``forecasterStub.test.ts``.
 */
export const forecasterStub: Forecaster = {
  forecast(_request: ForecastRequest): ForecastResult {
    return { points: [], method: 'unwired_stub', basis: 'forecast' };
  },
};
